//src/core/api/treatmentRoomApi.ts
import { serverApi } from "@core/api/core"
import { handleApiResponse } from "@core/api/core/utils"
import { TreatmentRoomSerializer } from "@/types/treatmentRoom"
import { TreatmentRoomSyncState, type TreatmentRoomSyncPayload, type TreatmentRoomWriteContext } from "@core/utils/treatmentRoomSync"

export const TREATMENT_ROOM_SYNC_EVENT = "treatment-room-sync"
let syncState = new TreatmentRoomSyncState()
let deltaSupported: boolean | null = null
let syncScope: string | null = null
let pendingDeltas: TreatmentRoomSyncPayload[] = []
let snapshotRequestId = 0
let appliedSnapshotRequestId = 0
let pendingSnapshotCount = 0
let snapshotResyncNeeded = false
let deferredSnapshotResync = false

interface PendingTreatmentRoomWrite {
  promise: Promise<void>
  data: TreatmentRoomSerializer
  continueOwnWrites: boolean
  context?: TreatmentRoomWriteContext
  acknowledged?: TreatmentRoomWriteContext
  patientUuid?: string
  vacated?: boolean
  moveSource?: boolean
  contexts: Map<number, string>
  previous?: PendingTreatmentRoomWrite
}

interface TreatmentRoomWriteAcknowledgement {
  context?: TreatmentRoomWriteContext
  additionalContext?: TreatmentRoomWriteContext
  delta?: TreatmentRoomSyncPayload
}

const pendingTreatmentRoomWrites = new Map<string, PendingTreatmentRoomWrite>()
const deferredTreatmentRoomBeds = new Set<string>()

const canContinueTreatmentRoomWrite = (
  pending: PendingTreatmentRoomWrite,
  previous: PendingTreatmentRoomWrite | undefined,
  latest: TreatmentRoomWriteContext | undefined
): boolean => {
  const context = pending.context
  const acknowledged = previous?.acknowledged
  return !!(
    pending.continueOwnWrites &&
    previous?.continueOwnWrites &&
    context &&
    previous.context &&
    acknowledged &&
    previous.context.epoch === context.epoch &&
    acknowledged.epoch === context.epoch &&
    (acknowledged.assignment === context.assignment ||
      (pending.patientUuid && pending.patientUuid === previous.patientUuid && pending.patientUuid === acknowledged.patientUuid) ||
      (previous.vacated && pending.data.treatmentRoomPatientUuid && acknowledged.assignment === "")) &&
    (previous.contexts.get(context.revision) === context.assignment ||
      (context.revision === acknowledged.revision && context.assignment === acknowledged.assignment)) &&
    latest?.epoch === acknowledged.epoch &&
    latest.revision === acknowledged.revision &&
    latest.assignment === acknowledged.assignment
  )
}

export const isTreatmentRoomDeltaSupported = () => deltaSupported === true
export const getTreatmentRoomWriteContext = (bed: string) => syncState.expected(bed)

const withPendingTreatmentRoomItems = (item: TreatmentRoomSerializer): TreatmentRoomSerializer => {
  const bed = item.treatmentRoomBedNumber || ""
  const pending = pendingTreatmentRoomWrites.get(bed)
  const current = syncState.expected(bed)
  let matchesAssignment = current?.assignment === pending?.context?.assignment
  if (pending && !matchesAssignment) {
    let previous = pending.previous
    while (previous?.continueOwnWrites && previous.patientUuid === pending.patientUuid) {
      if (canContinueTreatmentRoomWrite(pending, previous, current)) {
        matchesAssignment = true
        break
      }
      previous = previous.previous
    }
  }
  if (
    pending?.continueOwnWrites &&
    pending.data.treatmentRoomItems !== undefined &&
    pending.context &&
    current?.epoch === pending.context.epoch &&
    matchesAssignment
  ) {
    return { ...item, treatmentRoomItems: pending.data.treatmentRoomItems }
  }
  return item
}

const requestPendingDeltaResync = () => {
  if (!pendingDeltas.length && !snapshotResyncNeeded) {
    deferredSnapshotResync = false
    return
  }
  if (pendingSnapshotCount > 0) {
    deferredSnapshotResync = true
    return
  }
  deferredSnapshotResync = false
  window.dispatchEvent(new CustomEvent(TREATMENT_ROOM_SYNC_EVENT, { detail: { resync: true } }))
}

const shouldDeferTreatmentRoomBed = (bed: string): boolean => {
  const item = syncState.items[bed]
  const pending = pendingTreatmentRoomWrites.get(bed)
  if (!item && pending?.continueOwnWrites && pending.context?.epoch === syncState.epoch) {
    const latest = syncState.expected(bed)
    let assignment: PendingTreatmentRoomWrite | undefined = pending
    while (assignment?.continueOwnWrites && assignment.patientUuid === pending.patientUuid) {
      if (!assignment.moveSource && assignment.data.treatmentRoomPatientUuid && latest?.assignment === "" && !latest.patientUuid) {
        if (assignment.context?.revision === latest.revision && assignment.context.assignment === latest.assignment) return true
        const previous = assignment.previous
        if (
          previous?.moveSource &&
          ((previous.vacated && canContinueTreatmentRoomWrite(assignment, previous, latest)) ||
            (!previous.acknowledged &&
              assignment.context &&
              previous.context?.epoch === latest.epoch &&
              previous.contexts.get(assignment.context.revision) === assignment.context.assignment))
        )
          return true
      }
      assignment = assignment.previous
    }
  }
  if (
    item &&
    pending?.continueOwnWrites &&
    pending.context?.epoch === syncState.epoch &&
    pending.patientUuid === item.treatmentRoomPatientUuid &&
    (pending.moveSource || (pending.data.treatmentRoomItems !== undefined && withPendingTreatmentRoomItems(item) === item))
  ) {
    const latest = syncState.expected(bed)
    if (pending.moveSource && latest?.revision === pending.context.revision && latest.assignment === pending.context.assignment) return true
    let previous = pending.previous
    while (previous?.continueOwnWrites && previous.patientUuid === pending.patientUuid) {
      if (pending.moveSource && canContinueTreatmentRoomWrite(pending, previous, latest)) return true
      if (!previous.acknowledged && previous.contexts.get(pending.context.revision) === pending.context.assignment) return true
      previous = previous.previous
    }
  }
  return false
}

const publishTreatmentRoomBeds = (beds: string[]) => {
  const ready = [...new Set([...beds, ...deferredTreatmentRoomBeds])].filter(bed => {
    if (shouldDeferTreatmentRoomBed(bed)) {
      deferredTreatmentRoomBeds.add(bed)
      return false
    }
    deferredTreatmentRoomBeds.delete(bed)
    return true
  })
  if (ready.length)
    window.dispatchEvent(
      new CustomEvent(TREATMENT_ROOM_SYNC_EVENT, {
        detail: {
          beds: ready,
          items: ready
            .map(bed => syncState.items[bed])
            .filter(Boolean)
            .map(withPendingTreatmentRoomItems),
        },
      })
    )
}

export const receiveTreatmentRoomDelta = (delta: TreatmentRoomSyncPayload) => {
  if (delta.protocol !== 1) return
  if (deltaSupported === null) {
    if (pendingSnapshotCount > 0) {
      pendingDeltas.push(delta)
      requestPendingDeltaResync()
    }
    return
  }
  if (!deltaSupported) return
  const beds = syncState.applyDelta(delta)
  if (beds === null) {
    pendingDeltas.push(delta)
    requestPendingDeltaResync()
  } else publishTreatmentRoomBeds(beds)
}

export const getTreatmentRoomSnapshot = async (scope: string): Promise<TreatmentRoomSerializer[] | null> => {
  if (scope !== syncScope) {
    syncScope = scope
    syncState = new TreatmentRoomSyncState()
    deltaSupported = null
    pendingDeltas = []
    pendingSnapshotCount = 0
    snapshotResyncNeeded = false
    deferredSnapshotResync = false
    pendingTreatmentRoomWrites.clear()
    deferredTreatmentRoomBeds.clear()
  }
  const state = syncState
  const pendingAtRequestStart = new Set(pendingDeltas)
  const requestId = ++snapshotRequestId
  pendingSnapshotCount += 1
  const response = await serverApi
    .get("/treatment_room/", { params: { sync: "1" } })
    .finally(() => {
      if (state === syncState) pendingSnapshotCount -= 1
    })
    .catch(error => {
      if (state === syncState && deferredSnapshotResync) requestPendingDeltaResync()
      throw error
    })
  const result = handleApiResponse<TreatmentRoomSyncPayload | TreatmentRoomSerializer[]>(response)
  if (state !== syncState) return null
  if (Array.isArray(result)) {
    if (requestId < appliedSnapshotRequestId) {
      requestPendingDeltaResync()
      return deltaSupported ? Object.values(state.items).map(withPendingTreatmentRoomItems) : null
    }
    appliedSnapshotRequestId = requestId
    deltaSupported = false
    pendingDeltas = []
    snapshotResyncNeeded = false
    deferredSnapshotResync = false
    return result
  }
  if (result.protocol !== 1) throw new Error("지원하지 않는 치료실 동기화 응답입니다.")
  if (requestId < appliedSnapshotRequestId && result.epoch !== state.epoch) {
    snapshotResyncNeeded = true
    requestPendingDeltaResync()
    return null
  }
  appliedSnapshotRequestId = Math.max(appliedSnapshotRequestId, requestId)
  const previousBeds = Object.keys(state.items)
  if (state.applySnapshot(result)) snapshotResyncNeeded = false
  deltaSupported = true
  pendingDeltas = pendingDeltas.filter(delta => state.applyDelta(delta) === null && !pendingAtRequestStart.has(delta))
  if ([...previousBeds, ...Object.keys(state.versions), ...pendingTreatmentRoomWrites.keys()].some(shouldDeferTreatmentRoomBed)) {
    publishTreatmentRoomBeds([...previousBeds, ...Object.keys(state.versions), ...Object.keys(state.items)])
    requestPendingDeltaResync()
    return null
  }
  const items = Object.values(state.items).map(withPendingTreatmentRoomItems)
  window.dispatchEvent(new CustomEvent(TREATMENT_ROOM_SYNC_EVENT, { detail: { snapshot: true, items } }))
  requestPendingDeltaResync()
  return items
}

// ──────────────────────────────────────────────────────────────────────────────
// 치료실 목록조회
// ──────────────────────────────────────────────────────────────────────────────
export const getTreatmentRoomsInfo = async (): Promise<TreatmentRoomSerializer[]> => {
  const requestUrl = `/treatment_room/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<TreatmentRoomSerializer[]>(response)
}

const enqueueTreatmentRoomWrite = async (
  data: TreatmentRoomSerializer,
  syncContext: TreatmentRoomWriteContext | undefined,
  continueOwnWrites: boolean,
  save: (
    context?: TreatmentRoomWriteContext,
    additionalContext?: TreatmentRoomWriteContext
  ) => Promise<TreatmentRoomWriteAcknowledgement | undefined>,
  additional?: { data: TreatmentRoomSerializer; syncContext?: TreatmentRoomWriteContext },
  patientUuid?: string
): Promise<void> => {
  const state = syncState
  const entries = [
    { data, syncContext },
    ...(additional && additional.data.treatmentRoomBedNumber !== data.treatmentRoomBedNumber ? [additional] : []),
  ]
  const writes = entries.map(entry => {
    const bed = entry.data.treatmentRoomBedNumber || ""
    const pending: PendingTreatmentRoomWrite = {
      promise: Promise.resolve(),
      data: entry.data,
      continueOwnWrites,
      context: entry.syncContext ? { ...entry.syncContext } : undefined,
      patientUuid: entry.data.treatmentRoomPatientUuid ?? patientUuid,
      moveSource: entry === additional,
      contexts: new Map(),
      previous: pendingTreatmentRoomWrites.get(bed),
    }
    return { bed, pending, previous: pending.previous }
  })
  const promise = Promise.all(writes.map(write => write.previous?.promise || Promise.resolve())).then(async () => {
    if (state !== syncState || deltaSupported !== true) throw new Error("치료실 조회 대상이나 동기화 방식이 변경되었습니다. 다시 조회해 주세요.")
    for (const { bed, pending, previous } of writes) {
      let context = pending.context
      const acknowledged = previous?.acknowledged
      const latest = state.expected(bed)
      if (previous && canContinueTreatmentRoomWrite(pending, previous, latest)) {
        context = acknowledged
        pending.contexts = new Map(previous.contexts)
      }
      pending.context = context
      if (context) pending.contexts.set(context.revision, context.assignment)
    }
    const acknowledged = await save(writes[0].pending.context, writes[1]?.pending.context)
    writes[0].pending.acknowledged = acknowledged?.context
    if (writes[1] && acknowledged?.additionalContext) {
      writes[1].pending.acknowledged = acknowledged.additionalContext
      writes[1].pending.vacated = true
    }
    if (acknowledged?.delta) receiveTreatmentRoomDelta(acknowledged.delta)
  })
  writes.forEach(({ bed, pending }) => {
    pending.promise = promise
    pendingTreatmentRoomWrites.set(bed, pending)
    const release = () => {
      if (pendingTreatmentRoomWrites.get(bed) === pending) {
        pendingTreatmentRoomWrites.delete(bed)
        if (deferredTreatmentRoomBeds.has(bed)) publishTreatmentRoomBeds([bed])
      }
    }
    void promise.then(release, release)
  })
  await promise
}

export const withTreatmentRoomWriteContext = async (
  bed: string,
  expected: TreatmentRoomWriteContext | undefined,
  operation: (context?: TreatmentRoomWriteContext) => Promise<void>,
  continueOwnWrites = false,
  patientUuid?: string
): Promise<void> => {
  if (deltaSupported !== true) {
    await operation(expected)
    return
  }
  await enqueueTreatmentRoomWrite(
    { treatmentRoomBedNumber: bed },
    expected,
    continueOwnWrites,
    async context => {
      const latest = syncState.expected(bed)
      if (!context || !latest || context.epoch !== latest.epoch || context.revision !== latest.revision || context.assignment !== latest.assignment) {
        throw new Error("베드의 환자 또는 치료 상태가 변경되었습니다. 다시 확인해 주세요.")
      }
      await operation(context)
      return undefined
    },
    undefined,
    patientUuid
  )
}

export const updateTreatmentRoomsInfo = async (
  data: TreatmentRoomSerializer,
  moveFromBed?: string,
  expected?: TreatmentRoomWriteContext,
  moveExpected?: TreatmentRoomWriteContext,
  continueOwnWrites = expected === undefined,
  patientUuid?: string
): Promise<void> => {
  const requestUrl = `/treatment_room/`
  const state = syncState
  const supported = deltaSupported === true
  const bed = data.treatmentRoomBedNumber || ""
  const syncContext = supported ? expected || state.expected(bed) : undefined
  const sourceContext = supported && moveFromBed ? moveExpected || state.expected(moveFromBed) : undefined
  const save = async (
    context?: TreatmentRoomWriteContext,
    moveContext?: TreatmentRoomWriteContext
  ): Promise<TreatmentRoomWriteAcknowledgement | undefined> => {
    const source = supported && moveFromBed ? state.items[moveFromBed] : undefined
    if (supported && moveFromBed && source?.treatmentRoomPatientUuid !== data.treatmentRoomPatientUuid) {
      throw new Error("이동할 환자가 변경되었습니다. 다시 조회해 주세요.")
    }
    const response = await serverApi.patch(
      requestUrl,
      {
        ...data,
        ...(source ? { treatmentRoomItems: source.treatmentRoomItems, treatmentRoomStartedAt: source.treatmentRoomStartedAt } : {}),
        syncContext: context,
        ...(supported && moveFromBed ? { moveFromBed, moveContext } : {}),
      },
      { params: supported ? { sync: "1" } : undefined }
    )
    if (supported) {
      if (state !== syncState) return undefined
      const delta = handleApiResponse<TreatmentRoomSyncPayload>(response)
      const version = delta.bedVersions[bed]
      const sourceVersion = moveFromBed ? delta.bedVersions[moveFromBed] : undefined
      return {
        delta,
        context: version ? { epoch: delta.epoch, ...version } : undefined,
        additionalContext:
          moveFromBed && delta.deletedBeds?.includes(moveFromBed) && sourceVersion?.assignment === "" && !sourceVersion.patientUuid
            ? { epoch: delta.epoch, ...sourceVersion }
            : undefined,
      }
    }
    handleApiResponse<void>(response)
    return undefined
  }
  if (!supported || !bed) {
    const acknowledged = await save(syncContext)
    if (acknowledged?.delta) receiveTreatmentRoomDelta(acknowledged.delta)
    return
  }

  await enqueueTreatmentRoomWrite(
    data,
    syncContext,
    continueOwnWrites,
    save,
    moveFromBed
      ? { data: { treatmentRoomBedNumber: moveFromBed, treatmentRoomPatientUuid: data.treatmentRoomPatientUuid }, syncContext: sourceContext }
      : undefined,
    patientUuid
  )
}

export const moveTreatmentRoom = async (
  sourceBed: string,
  target: TreatmentRoomSerializer,
  sourceExpected?: TreatmentRoomWriteContext,
  targetExpected?: TreatmentRoomWriteContext
): Promise<void> => {
  if (deltaSupported === null) {
    const scope = syncScope ?? ""
    const snapshot = await getTreatmentRoomSnapshot(scope)
    if (snapshot === null || scope !== syncScope) throw new Error("치료실 조회 대상이 변경되었습니다. 다시 조회해 주세요.")
  }
  const state = syncState
  const atomic = deltaSupported === true
  await updateTreatmentRoomsInfo(target, atomic ? sourceBed : undefined, targetExpected, sourceExpected, true)
  if (!atomic) {
    if (state !== syncState || deltaSupported !== false) {
      throw new Error("치료실 동기화 방식이 변경되었습니다. 다시 조회해 주세요.")
    }
    await deleteTreatmentRoom(sourceBed, sourceExpected)
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// 치료종료- > 베드 삭제
// ──────────────────────────────────────────────────────────────────────────────
export const deleteTreatmentRoom = async (treatmentBedNumber: string, expected?: TreatmentRoomWriteContext): Promise<void> => {
  const requestUrl = `/treatment_room/${treatmentBedNumber}/`
  const state = syncState
  const supported = deltaSupported === true
  const response = await serverApi.delete(requestUrl, {
    data: supported ? { syncContext: expected || state.expected(treatmentBedNumber) } : undefined,
    params: supported ? { sync: "1" } : undefined,
  })
  if (supported) {
    if (state === syncState) receiveTreatmentRoomDelta(handleApiResponse<TreatmentRoomSyncPayload>(response))
    return
  }
  return handleApiResponse<void>(response)
}

export const stopTreatmentEndAlarmBroadcast = async (): Promise<void> => {
  const requestUrl = `/treatment_room/alarm-stop/`
  const response = await serverApi.post(requestUrl)
  return handleApiResponse<void>(response)
}
